import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { renderAsync } from "docx-preview";
import { Box, Typography } from "@mui/material";
import { api } from "../../../../services";
import DocumentLoading from "./DocumentLoading";

/**
 * DocumentRenderer
 *
 * This component handles the client-side rendering of document files, specifically Word documents (.docx)
 * and plain text files (.txt). It uses the docx-preview library to transform binary data into
 * a readable document layout within the application's media viewer.
 */
interface DocumentRendererProps {
  url?: string;
  file?: File;
  filename?: string;
  mode?: "preview" | "thumbnail" | "dialog" | "expanded";
  width?: string | number;
  height?: string | number;
  onLoad?: () => void;
  onError?: (error?: any) => void;
  onClick?: () => void;
}

export const DocumentRenderer: React.FC<DocumentRendererProps> = React.memo(
  ({ url, file, filename, mode = "preview", width = "100%", height = "100%", onLoad, onError, onClick }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [loadProgress, setLoadProgress] = useState(0);

    const isDocx = useMemo(() => {
      const name = filename || file?.name || url || "";
      return name.toLowerCase().endsWith(".docx");
    }, [filename, file, url]);

    const isTxt = useMemo(() => {
      const name = filename || file?.name || url || "";
      return name.toLowerCase().endsWith(".txt");
    }, [filename, file, url]);

    const renderDocument = useCallback(async () => {
      if (!containerRef.current) return;

      try {
        setIsLoading(true);
        setError(null);
        setLoadProgress(10);

        let data: ArrayBuffer | Blob;

        if (file) {
          // If we already have the File object (e.g., from a fresh upload), use it directly
          data = file;
          setLoadProgress(30);
        } else if (url) {
          /**
           * We use the project's standard 'api' service instead of a raw 'fetch'.
           * This is critical because the 'api' interceptors automatically decrypt
           * our accessToken and add it to the headers, ensuring we can access
           * protected documents stored in our cloud storage.
           */
          const response = await api.get(url, {
            responseType: "arraybuffer",
            withCredentials: true
          });

          data = response.data;
          setLoadProgress(50);
        } else {
          throw new Error("No document source provided");
        }

        // Logic branching based on file type
        if (isDocx) {
          setLoadProgress(70);
          /**
           * Render the docx content into our containerRef.
           * docx-preview handles the heavy lifting of parsing XML styles into HTML/CSS.
           */
          await renderAsync(data, containerRef.current, undefined, {
            inWrapper: true,
            ignoreWidth: false,
            ignoreHeight: false,
            ignoreFonts: false,
            breakPages: true,
            ignoreLastRenderedPageBreak: true,
            experimental: false,
            trimXmlDeclaration: true,
            useBase64URL: false,
            debug: false
          });
          setLoadProgress(100);
        } else if (isTxt) {
          setLoadProgress(70);
          /**
           * For plain text files, we simply decode the binary data
           * and display it inside a <pre> tag to maintain formatting.
           */
          const text = data instanceof Blob ? await data.text() : new TextDecoder().decode(data);
          containerRef.current.innerHTML = `<pre style="white-space: pre-wrap; word-wrap: break-word; padding: 20px; font-family: monospace;">${text}</pre>`;
          setLoadProgress(100);
        } else {
          const name = filename || file?.name || url || "";
          setError(`Preview not supported for ${name.split(".").pop()} files. Please download to view.`);
          setLoadProgress(0);
        }

        setIsLoading(false);
        if (onLoad) onLoad();
      } catch (err: any) {
        console.error("[DocumentRenderer] Error rendering document:", err);
        setError(err.message || "Failed to render document");
        setIsLoading(false);
        setLoadProgress(0);
        if (onError) onError(err);
      }
    }, [url, file, isDocx, isTxt, filename, onLoad, onError]);

    useEffect(() => {
      renderDocument();
    }, [renderDocument]);

    if (mode === "thumbnail") {
      return (
        <Box
          sx={{
            width,
            height,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0,0,0,0.1)",
            borderRadius: 1,
            cursor: onClick ? "pointer" : "default"
          }}
          onClick={onClick}
        >
          <Typography variant='caption'>📄 DOC</Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          height: "100%",
          maxHeight: "80dvh",
          overflow: "hidden",
          pointerEvents: "auto",
          bgcolor: "#2d333e"
        }}
        onClick={onClick}
      >
        {isLoading && <DocumentLoading progress={loadProgress} />}

        {error && (
          <Box sx={{ margin: 2, p: 2, bgcolor: "error.light", borderRadius: 1 }}>
            <Typography color='error'>{error}</Typography>
          </Box>
        )}

        <Box
          ref={containerRef}
          className='scroller'
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            width: "100%",
            maxWidth: "100%",
            flex: 1,
            position: "relative",
            overflow: "auto",
            bgcolor: "#fff",
            visibility: isLoading ? "hidden" : "visible",

            "& .docx-wrapper": {
              backgroundColor: "transparent",
              padding: "20px",
              width: "100%"
            },
            "& .docx": {
              boxShadow: "0 2px 8px rgba(0,0,0,0.15) !important",
              margin: "0 auto !important",
              maxWidth: "100% !important",
              padding: "20px !important",
              minHeight: "100%"
            }
          }}
        />
      </Box>
    );
  }
);

export default DocumentRenderer;
